/**
 * The FAAB ledger: what each manager holds, and every line that got him there.
 *
 * ONE RULE ABOVE ALL — A BALANCE IS NEVER STORED. It is `openingBalance(gm)` plus every entry,
 * computed here on every read. The app used to seed everybody at the ₣100 base, which overstated
 * Luke by ₣39 and understated Samant by ₣40 before a single transaction was recorded, and a stored
 * balance is how that survives a correction: fix the rollover rule or the 2025 ending balances and
 * every opening reprices, with nothing stale left behind for somebody to remember to edit.
 *
 * The two opening lines per manager are the same idea one level down. They are DERIVED from
 * `openingLedger()` rather than written as rows, so the column adds up from zero and the two facts
 * people actually argue about — what he had left, and what the league gave him — are visible
 * entries rather than a number that arrived from nowhere.
 */

import { faab, openingBalance, openingLedger } from './faab.js';
import { isVoid, voidedIds } from './faabVoid.js';
import type { FaabEntry } from '../store/types.js';

/**
 * How a line reads. The store's `kind` is wider; this is what the ledger displays.
 *
 * `trade` earns its own word because it is the one movement with a counterparty. Reading "₣10
 * adjustment" against a deal the archive records by name told a man his balance had changed and not
 * who changed it — see `domain/tradeFaab.ts`.
 *
 * `luxury` likewise (Tim, 9 September: "instead of 'Adjustment', luxury assessments should have
 * their own category"). It is the largest recurring charge in the league, it is assessed from a
 * man's own slate rather than decided by anybody, and seven of them land on one day — reading that
 * day as seven adjustments says a commissioner made seven decisions when the rules made one.
 *
 * `void` is not a kind of movement at all, it is the cancellation of one, and it reads as its own
 * line so the ledger can be seen to add up: the original stays on the page struck through and the
 * reversal sits under it carrying the opposite amount. Hiding either half would make the column
 * stop matching the balance.
 */
export type LedgerKind =
  'rollover' | 'allotment' | 'purchase' | 'trade' | 'luxury' | 'farm' | 'adjustment' | 'void';

export interface LedgerLine {
  /** The stored entry's id, which is what a Delete or an Edit acts on. Absent on the openings. */
  id?: string;
  gm: string;
  kind: LedgerKind;
  amount: number;
  description: string;
  /** ISO day. Openings all land on 1 January, which is why the ledger groups by day. */
  date: string;
  /** The rule behind this kind of line, where there is one. Rendered as a `§`. */
  rule?: string;
  /** False for the derived openings, which cannot be edited line by line. */
  stored: boolean;
  /** Some reversal in the ledger cancels this line. It still counts; it reads struck through. */
  voided?: boolean;
  /** This line IS a reversal, and this is the id it cancels. */
  voids?: string;
}

export const KIND_LABEL: Readonly<Record<LedgerKind, string>> = {
  rollover: 'Rollover',
  allotment: 'Allotment',
  purchase: 'Purchase',
  trade: 'Trade',
  luxury: 'Luxury tax',
  farm: 'Farm bid',
  adjustment: 'Adjustment',
  void: 'Reversed',
};

/**
 * The rule each kind answers to. `allotment` and `adjustment` have none on purpose: the base
 * allotment is the league's own constant, and an adjustment is the one kind with no rule behind
 * it — which is why the entry bar refuses to save one without a reason.
 */
export const KIND_RULE: Readonly<Partial<Record<LedgerKind, string>>> = {
  rollover: '2018-II-4',
  purchase: '2020-I-6',
  luxury: '2023-keeper-expansion-eight',
  farm: '2019-II-6',
};

/**
 * A stored entry's `kind` is about what HAPPENED; a ledger line's is about how it READS. Eight
 * stored kinds collapse to two here — anything that credits scrip a GM bought is a purchase, and
 * everything else that moves a balance without being an opening reads as an adjustment. Keeping
 * eight columns of jargon in front of a reader who wants to know why his balance changed would be
 * showing him the schema.
 */
export function lineKind(stored: FaabEntry['kind']): LedgerKind {
  if (stored === 'purchase') return 'purchase';
  if (stored === 'allotment') return 'allotment';
  if (stored === 'trade') return 'trade';
  if (stored === 'luxury-tax') return 'luxury';
  if (stored === 'farm-bid') return 'farm';
  return 'adjustment';
}

/** Every line for a season: the derived openings first, then what was recorded, oldest first. */
export function ledgerFor(entries: readonly FaabEntry[], gms?: readonly string[]): LedgerLine[] {
  const opening: LedgerLine[] = openingLedger(gms).map((o) => ({
    gm: o.gm,
    kind: o.kind,
    amount: o.amount,
    description: o.description,
    date: o.date,
    ...(KIND_RULE[o.kind] ? { rule: KIND_RULE[o.kind]! } : {}),
    stored: false,
  }));
  const cancelled = voidedIds(entries);
  const moves: LedgerLine[] = entries.map((e) => {
    /* A reversal reads as a reversal whatever it reverses — see `LedgerKind`. */
    const kind = isVoid(e) ? 'void' : lineKind(e.kind);
    return {
      id: e.id,
      gm: e.gm.toUpperCase(),
      kind,
      amount: e.amount,
      description: e.description,
      date: e.date.slice(0, 10),
      ...(KIND_RULE[kind] ? { rule: KIND_RULE[kind]! } : {}),
      stored: true,
      ...(cancelled.has(e.id) ? { voided: true } : {}),
      ...(e.voids ? { voids: e.voids } : {}),
    };
  });
  return pairVoids([...opening, ...moves]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)));
}

/**
 * A reversal sits directly under the line it reverses, wherever the dates put them.
 *
 * The reversal is dated the day it was made, and that is the honest date — the commissioner noticed
 * in September that an August row was wrong, and a ledger that back-dated the correction to August
 * would be a ledger with no record of when it was corrected, which is the whole thing the
 * append-only rule exists to keep.
 *
 * But dated honestly the two halves land a month apart, and then the column does not add up
 * anywhere a reader is looking: August shows a charge that was cancelled and September shows a
 * credit for no visible reason. So the order is chronological EXCEPT that a cancellation follows its
 * original, which is how a ledger is read rather than how it is stored. Both dates stay on the page.
 */
export function pairVoids(lines: readonly LedgerLine[]): LedgerLine[] {
  if (!lines.some((l) => l.kind === 'void')) return [...lines];
  const voidsOf = new Map<string, LedgerLine[]>();
  for (const l of lines) {
    if (l.kind !== 'void' || !l.voids) continue;
    const at = voidsOf.get(l.voids) ?? [];
    at.push(l);
    voidsOf.set(l.voids, at);
  }
  const out: LedgerLine[] = [];
  for (const l of lines) {
    if (l.kind === 'void' && l.voids && lines.some((x) => x.id === l.voids)) continue;
    out.push(l);
    if (l.id) out.push(...(voidsOf.get(l.id) ?? []));
  }
  return out;
}

/**
 * The kind a line counts as: its own, or — for a reversal — the kind of the line it cancels.
 *
 * A reversed purchase has to keep counting as a purchase in both directions, or `Bought` on the
 * standings row shows scrip a man paid for and got refunded.
 */
export function originKind(line: LedgerLine, all: readonly LedgerLine[]): LedgerKind {
  if (line.kind !== 'void' || !line.voids) return line.kind;
  const orig = all.find((l) => l.id === line.voids);
  return orig ? originKind(orig, all) : 'adjustment';
}

/** Newest line first, or oldest. The ledger has been read both ways since it was one column. */
export type LedgerOrder = 'new' | 'old';

/**
 * The lines in reading order.
 *
 * Tim, 9 September: "give GM txn list a sort for recent<->oldest; give ledger the same sort
 * option." A ledger is stored oldest-first because that is how a balance accumulates, and read
 * newest-first for most of a season because what changed today is the question. Both, then, and
 * the toggle rather than a default that is wrong half the year.
 *
 * A REVERSAL STAYS UNDER ITS ORIGINAL EITHER WAY. Reversing a paired list puts the cancellation
 * ABOVE the line it cancels, which reads as a credit somebody then spent. So the pairs are put
 * back after the flip — the same reason `pairVoids` exists at all.
 */
export function sortLines(lines: readonly LedgerLine[], order: LedgerOrder): LedgerLine[] {
  if (order === 'old') return [...lines];
  return pairVoids([...lines].reverse());
}

export interface GmStanding {
  gm: string;
  /** Rollover plus the base — where the ledger starts. */
  opening: number;
  /** Scrip bought with real money this season. Zero renders as a dash, not as `+₣0`. */
  bought: number;
  balance: number;
  lines: LedgerLine[];
}

/** Every manager, richest first — the order the table is read in. */
export function standings(entries: readonly FaabEntry[], gms: readonly string[]): GmStanding[] {
  const all = ledgerFor(entries, gms);
  return gms
    .map((raw) => {
      const gm = raw.toUpperCase();
      const lines = all.filter((l) => l.gm === gm);
      const opening = openingBalance(gm);
      const moved = lines.filter((l) => l.stored).reduce((n, l) => n + l.amount, 0);
      return {
        gm,
        opening,
        /* A reversed purchase counts in both directions — see `originKind`. */
        bought: lines.filter((l) => originKind(l, all) === 'purchase')
          .reduce((n, l) => n + l.amount, 0),
        balance: opening + moved,
        lines,
      };
    })
    .sort((a, b) => b.balance - a.balance || a.gm.localeCompare(b.gm));
}

export type LedgerFilter =
  'all' | 'openings' | 'purchase' | 'trade' | 'luxury' | 'farm' | 'adjustment';

/**
 * `openings` is two kinds, which is why this is a function rather than an equality check.
 *
 * A REVERSAL FILTERS WITH THE LINE IT REVERSES, not on its own. Filtering to Trades and being shown
 * a trade struck through with no sign of what cancelled it is the filter answering a question nobody
 * asked; and a `Reversed` chip of its own would be a fifth filter for a thing that is never looked
 * for by itself. So the void rows are matched by the kind of their original, which `ledgerFor`
 * cannot know and `matchesFilter` is told through the whole list.
 */
export function matchesFilter(
  line: LedgerLine,
  filter: LedgerFilter,
  all?: readonly LedgerLine[],
): boolean {
  if (filter === 'all') return true;
  const kind = all ? originKind(line, all) : line.kind;
  if (filter === 'openings') return kind === 'rollover' || kind === 'allotment';
  return kind === filter;
}

export interface DayGroup {
  date: string;
  lines: LedgerLine[];
  /** What the day did to the league's total, which is the only number that reads at a glance. */
  net: number;
}

/**
 * Grouped by day, because twenty of the twenty-four 2026 lines land on 1 January — they are the
 * derived openings. Ungrouped, the ledger reads as one undifferentiated column in which the four
 * things that actually happened are lost among twenty that did not.
 */
export function byDay(lines: readonly LedgerLine[]): DayGroup[] {
  const out: DayGroup[] = [];
  for (const line of [...lines].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))) {
    const last = out[out.length - 1];
    if (last && last.date === line.date) { last.lines.push(line); last.net += line.amount; }
    else out.push({ date: line.date, lines: [line], net: line.amount });
  }
  return out;
}

/** `+₣25`, `−₣22`, and a dash for nothing — a zero-value entry is a note, not a movement. */
export function amountLabel(n: number): string {
  if (n === 0) return '—';
  return `${n < 0 ? '−' : '+'}${faab(Math.abs(n))}`;
}

/** `1 January 2026`. Long month, no leading zero — a group header, not a timestamp. */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return `${d} ${months[(m ?? 1) - 1]} ${y}`;
}

/**
 * A sparkline of one manager's season, as an SVG path.
 *
 * The vertical scale is FIXED at `max` rather than fitted to each manager's own range, and that is
 * the whole point: ten sparklines each fitted to themselves would make a GM who drifted from 101
 * to 99 look exactly like one who fell from 140 to 40. Shared scale, comparable shapes.
 */
export function sparkline(
  values: readonly number[],
  { width, height, max }: { width: number; height: number; max: number },
): string {
  if (values.length === 0) return '';
  if (values.length === 1) {
    const y = (height - (values[0]! / max) * height).toFixed(1);
    return `M0 ${y}L${width} ${y}`;
  }
  return values
    .map((v, i) => {
      const x = ((i / (values.length - 1)) * width).toFixed(1);
      const y = (height - (Math.max(0, v) / max) * height).toFixed(1);
      return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
    })
    .join('');
}

/** The running balance after each line — what the sparkline plots. */
export function runningBalance(standing: GmStanding): number[] {
  const out: number[] = [standing.opening];
  let n = standing.opening;
  for (const l of standing.lines.filter((x) => x.stored)) { n += l.amount; out.push(n); }
  return out;
}
